import React from 'react';
import { AlertTriangle, X } from 'lucide-react';

/**
 * Reusable Duplicate Confirmation Modal
 * Shows when duplicate records are detected during bulk upload
 */
const DuplicateConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  duplicateCount,
  newRecordsCount,
  recordType = 'records',
  duplicateDetails = [],
  loading = false
}) => {
  if (!isOpen) return null;

  const handleSkip = () => {
    onConfirm('skip');
  };

  const handleOverwrite = () => {
    onConfirm('overwrite');
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Duplicate Records Detected</h3>
              <p className="text-sm text-gray-600 mt-1">
                Found {duplicateCount} duplicate {recordType} in your upload
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            disabled={loading}
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          {/* Summary */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-sm text-green-700 mb-1">New Records</div>
              <div className="text-2xl font-bold text-green-600">{newRecordsCount}</div>
              <div className="text-xs text-green-600 mt-1">Will be added</div>
            </div>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="text-sm text-yellow-700 mb-1">Duplicate Records</div>
              <div className="text-2xl font-bold text-yellow-600">{duplicateCount}</div>
              <div className="text-xs text-yellow-600 mt-1">Already exist</div>
            </div>
          </div>

          {/* Duplicate Details Preview */}
          {duplicateDetails.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-semibold text-gray-700 mb-3">Duplicate Records Preview:</h4>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-60 overflow-y-auto">
                <div className="space-y-2">
                  {duplicateDetails.slice(0, 10).map((dup, idx) => (
                    <div key={idx} className="text-sm text-gray-700 flex items-center justify-between py-2 border-b border-gray-200 last:border-0">
                      <span className="font-medium">{dup.student_name || dup.name}</span>
                      <span className="text-gray-500">{dup.date || dup.readable_id}</span>
                    </div>
                  ))}
                  {duplicateDetails.length > 10 && (
                    <div className="text-xs text-gray-500 text-center pt-2">
                      + {duplicateDetails.length - 10} more duplicates
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="text-sm font-semibold text-blue-800 mb-2">What would you like to do?</h4>
            <ul className="text-sm text-blue-700 space-y-2">
              <li className="flex items-start gap-2">
                <span className="font-semibold min-w-[80px]">Skip:</span>
                <span>Keep existing records unchanged and add only new ones</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="font-semibold min-w-[80px]">Overwrite:</span>
                <span>Replace existing records with new data from your upload</span>
              </li>
            </ul>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleSkip}
              disabled={loading}
              className="flex-1 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : `Skip Duplicates (Keep Existing)`}
            </button>
            <button
              onClick={handleOverwrite}
              disabled={loading}
              className="flex-1 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Processing...' : `Overwrite Duplicates (Update)`}
            </button>
          </div>

          <button
            onClick={onClose}
            disabled={loading}
            className="w-full mt-3 px-6 py-2 text-gray-600 hover:text-gray-800 font-medium transition-colors disabled:opacity-50"
          >
            Cancel Upload
          </button>
        </div>
      </div>
    </div>
  );
};

export default DuplicateConfirmationModal;
